import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar } from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import { routes } from '../../config/utility';

const ReviewCard = ({ review }) => {
  // Extract main fields from review
  const {
    id,
    feedback,
    rating,
    service_type: serviceType, // e.g. "Delivery" or "Walk"
    service_id: serviceId, // e.g. "Delivery" or "Walk"
    user,
    walker_or_rider: walkerRider // The additional object we want to display
  } = review;

  // Destructure user fields
  const {
    name: userName = 'Unknown User',
    phone: userPhone = 'N/A',
    email: userEmail = 'N/A',
    join_at: userJoinDate = 'N/A',
    profile_photo: userProfilePhoto
  } = user || {};

  // Destructure walkerOrRider fields (if present)
  const {
    name: wrName = 'Unknown',
    phone: wrPhone = 'N/A',
    email: wrEmail = 'N/A',
    join_at: wrJoinDate = 'N/A',
    profile_photo: wrProfilePhoto
  } = walkerRider || {};

  // Helper to render star icons
  const renderStars = (ratingValue) => {
    return (
      <div className="flex items-center space-x-1">
        {[...Array(5)].map((_, index) => (
          <FontAwesomeIcon
            key={index}
            icon={faStar}
            className={index < ratingValue ? 'text-yellow-400' : 'text-gray-300'}
          />
        ))}
      </div>
    );
  };

  function getServiceLink(serviceType, serviceId) {
    if (serviceType === 'Walk') {
      return routes.walk_single.replace(':walk_id?', serviceId);
    } else if (serviceType === 'Delivery') {
      return routes.delivery_single.replace(':delivery_id?', serviceId);
    } 
  }
  

  return (
    <Link to={getServiceLink(serviceType, serviceId)}>
      <div
        className="relative w-full bg-white p-5 rounded-xl shadow-lg 
                 overflow-hidden transition-transform transform hover:-translate-y-1 hover:shadow-2xl"
      >
        {/* Soft gradient background overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-green-50 via-white to-gray-50 opacity-80 pointer-events-none"></div>

        {/* Main content (above gradient) */}
        <div className="relative z-50">
          {/* Header: Avatar, Name, Rating */}
          <div className="flex items-center space-x-4">
            {/* User Avatar */}
            <img
              src={userProfilePhoto || 'https://via.placeholder.com/80'}
              alt={userName}
              className="w-16 h-16 rounded-full object-cover border border-gray-200 shadow-sm"
            />
            {/* Name & Join Date */}
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-800">
                {userName}
              </h3>
              <p className="text-sm text-gray-500">Joined: {userJoinDate}</p>
            </div>
            {/* Star Rating */}
            <div className="text-right">
              {renderStars(rating)}
              <p className="text-xs text-gray-500">({rating}/5)</p>
            </div>
          </div>

          {/* Service Type Badge */}
          <div className="mt-3">
            <span
              className="inline-block px-2 py-1 text-xs font-semibold 
                       text-purple-600 bg-purple-100 rounded-full"
            >
              {serviceType || 'N/A'}
            </span>
          </div>

          {/* Feedback Text */}
          <p className="mt-3 text-gray-700 italic">
            {feedback || 'No feedback text provided.'}
          </p>


          {/* Walker/Rider Section (only show if walkerRider is present) */}
          {walkerRider && (
            <div className="mt-6 p-4 border border-gray-200 rounded-md bg-gray-50">
              <div className="flex items-center space-x-3 mb-3">
                <img
                  src={wrProfilePhoto || 'https://via.placeholder.com/80'}
                  alt={wrName}
                  className="w-12 h-12 rounded-full object-cover border border-gray-200"
                />
                <div>
                  <h4 className="text-md font-semibold text-gray-800">
                    {wrName}
                  </h4>
                  <p className="text-xs text-gray-500">
                    Joined: {wrJoinDate}
                  </p>
                </div>
              </div>

              <div className="text-sm text-gray-600 space-y-1">
                <p>
                  <strong>Email:</strong> {wrEmail}
                </p>
                <p>
                  <strong>Phone:</strong> {wrPhone}
                </p>
              </div>
            </div>
          )}

          {/* Subtle Footer - ID */}
          <div className="mt-4 text-xs text-gray-400 text-right">
            Feedback ID: {id}
          </div>
        </div>
      </div>
    </Link>

  );
};

export default ReviewCard;
